"use client";

import { useRef } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { Clock, Bug, Brain, Repeat, AlertTriangle, TrendingDown } from "lucide-react";
import { useReducedMotion } from "@/hooks/useReducedMotion";

const painPoints = [
  {
    icon: Clock,
    stat: "40%",
    label: "Time Lost Debugging",
    description: "Developers waste nearly half their time fixing bugs instead of building features",
    color: "#ef4444",
  },
  {
    icon: Repeat,
    stat: "67%",
    label: "Repetitive Tasks",
    description: "Most coding involves writing boilerplate that AI could generate instantly",
    color: "#f97316",
  },
  {
    icon: Brain,
    stat: "23min",
    label: "Context Switch Cost",
    description: "Every interruption costs 23 minutes to regain focus and deep work state",
    color: "#eab308",
  },
];

export function ProblemSection() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });
  const reducedMotion = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  return (
    <section
      ref={ref}
      id="problem"
      className="relative min-h-screen w-full py-32 overflow-hidden bg-[#1a1919]"
    >
      {/* Dramatic dark gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-[#252423] via-[#1a1919] to-[#0f0f0f]" />

        {/* Red warning glow */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-[120px]"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 4, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-orange-500/20 rounded-full blur-[100px]"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </div>

      {/* Glitch grid effect */}
      <motion.div
        className="absolute inset-0 opacity-10"
        style={{ y: bgY }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(239, 68, 68, 0.5) 1px, transparent 1px),
              linear-gradient(90deg, rgba(239, 68, 68, 0.5) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </motion.div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm font-medium text-red-400">The Developer Struggle</span>
          </motion.div>

          <motion.h2
            className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="block text-white">BUILDING SOFTWARE</span>
            <span className="block bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
              SHOULDN&apos;T BE THIS HARD
            </span>
          </motion.h2>

          <motion.p
            className="max-w-2xl mx-auto text-lg text-white/50"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Traditional development is slow, frustrating, and full of repetitive tasks
          </motion.p>
        </div>

        {/* Pain Points Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {painPoints.map((point, index) => (
            <motion.div
              key={index}
              className="relative group"
              initial={{ opacity: 0, y: 50, rotateX: -15 }}
              animate={isInView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
              transition={{
                delay: reducedMotion ? 0 : 0.3 + index * 0.15,
                duration: 0.8,
                ease: [0.215, 0.61, 0.355, 1],
              }}
              style={{ perspective: "1000px" }}
            >
              {/* Card glow */}
              <div
                className="absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm"
                style={{ background: `linear-gradient(135deg, ${point.color}40, transparent)` }}
              />

              <div className="relative p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden">
                {/* Background gradient */}
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full blur-[60px] opacity-30"
                  style={{ background: point.color }}
                />

                {/* Icon */}
                <motion.div
                  className="w-14 h-14 mb-6 rounded-xl flex items-center justify-center"
                  style={{ background: `${point.color}20`, border: `1px solid ${point.color}40` }}
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <point.icon className="w-7 h-7" style={{ color: point.color }} />
                </motion.div>

                {/* Stat with counter animation */}
                <motion.div
                  className="text-5xl md:text-6xl font-black mb-3"
                  style={{ color: point.color }}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : {}}
                  transition={{
                    delay: reducedMotion ? 0 : 0.5 + index * 0.15,
                    duration: 0.5,
                    type: "spring",
                    stiffness: 200,
                  }}
                >
                  {point.stat}
                </motion.div>

                {/* Label */}
                <h3 className="text-xl font-bold mb-3 text-white">
                  {point.label}
                </h3>

                {/* Description */}
                <p className="text-sm text-white/50 leading-relaxed">
                  {point.description}
                </p>

                {/* Bottom accent */}
                <motion.div
                  className="absolute bottom-0 left-0 h-1 rounded-full"
                  style={{ background: point.color }}
                  initial={{ width: "0%" }}
                  animate={isInView ? { width: "60%" } : {}}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 1, duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white/5 border border-white/10 rounded-full">
            <TrendingDown className="w-5 h-5 text-red-400" />
            <span className="text-white/60">There has to be a better way...</span>
            <motion.span
              className="text-2xl"
              animate={{ y: [0, -3, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              👇
            </motion.span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ProblemSection;
